import { axiosInstance } from "../../utils/axios";
import { DocumentPart } from "../models";
import { DocumentParameters } from "../../components/document/ParametersDocument";
import { DocumentPartMetadata } from "../models/document-part-metadata";
import { StructureDocumentPart } from "../models/structure-document-part.ts";
import { TgkElaborationResponse } from "../models/tgk-elaboration-response";
import { TgkChangeDocStructureElaborationRequest } from "../models/tgk-change-doc-structure-elaboration-request";
import { EmbeddedRoots } from "../models/embedded-roots";
import { DocumentPartIdNamePair } from "../models/document-part-id-name-pair";
import { DocumentPosition } from "../models/document-position";
import { DocumentPartWithStatus } from "../models/document-part-with-status";
import { RootState, store } from "../../redux/store/store";
import { selectExternalConfigs } from "../../redux/feature/settings/configurations-slice";

export interface DocumentOutlineItem {
  id?: string;
  name?: string;
  description?: string;
  type: "part" | "section";
  driveItem?: string;
  siteId?: string;
  hasChildren: boolean;
  status?: string;
}

export interface CallbackMappingMessage {
  status: string;
  details: string[];
}

const BASE_PATH: string = "/documentpart";

export function fetchDocumentPartNode(Id: string): Promise<DocumentPart[]> {
  return (
    axiosInstance
      .get(`${BASE_PATH}/${Id}/children`)
      .then((resp) => resp.data) // eslint-disable-next-line no-undef
      // eslint-disable-next-line no-undef
      .catch((reason) => console.log(`Error on fetching remote data: ${reason}`))
  );
}

export function fetchDocumentPartNodeWithPermissions(Id: string, page: number): Promise<EmbeddedRoots> {
  const ITEMS_PER_PAGE = 20;
  return (
    axiosInstance
      .get(`${BASE_PATH}/${Id}/childrenwithpermissions?page=${page}&size=${ITEMS_PER_PAGE}`)
      .then((resp) => resp.data) // eslint-disable-next-line no-undef
      // eslint-disable-next-line no-undef
      .catch((reason) => console.log(`Error on fetching remote data: ${reason}`))
  );
}

export function fetchDocumentPartStatus(rootId: string, documentIds: string[]): Promise<DocumentPartWithStatus[]> {
  return (
    axiosInstance
      .post(`${BASE_PATH}/rootId/${rootId}/workflowActionStatus`, documentIds, { params: { notShowLoading: true } })
      .then((resp) => resp.data) // eslint-disable-next-line no-undef
      // eslint-disable-next-line no-undef
      .catch((reason) => console.log(`Error on fetching remote data: ${reason}`))
  );
}

export function fetchActionWorkflow(rootId: string, Id: string, op: string): Promise<DocumentPartWithStatus[]> {
  return axiosInstance
    .post(`${BASE_PATH}/${Id}/root/${rootId}/workflowaction/${op}`, {}, { params: { notShowLoading: true } })
    .then((resp) => resp.data); // eslint-disable-next-line no-undef
  // eslint-disable-next-line no-undef
}

export function fetchAzureDefaultSiteFromExternalConfig(): Promise<any> {
  const state: RootState = store.getState();
  const externalConfigs = selectExternalConfigs(state);
  let azureDefaultSite = externalConfigs?.azureDefaultSite;
  let sharePointHostName = externalConfigs?.sharePointHostName;
  const result = { azureDefaultSite: azureDefaultSite, sharePointHostName: sharePointHostName };
  return Promise.resolve(result);
}

export function fetchDocumentPartDetails(Id: string): Promise<DocumentPart> {
  return (
    axiosInstance
      .get(`${BASE_PATH}/${Id}`)
      .then((resp) => resp.data) // eslint-disable-next-line no-undef
      // eslint-disable-next-line no-undef
      .catch((reason) => console.log(`Error on fetching remote data: ${reason}`))
  );
}

export function fetchActiveDocumentParameters(Id: string): Promise<DocumentParameters[]> {
  return (
    axiosInstance
      .get(`${BASE_PATH}/${Id}/parameters`)
      .then((resp) => resp.data) // eslint-disable-next-line no-undef
      // eslint-disable-next-line no-undef
      .catch((reason) => console.log(`Error on fetching remote data: ${reason}`))
  );
}

export function fetchManageDocumentPartOutline(id: string): Promise<StructureDocumentPart> {
  return (
    axiosInstance
      .get(`structuredocumentpart/${id}`)
      .then((resp) => resp.data) // eslint-disable-next-line no-undef
      // eslint-disable-next-line no-undef
      .catch((reason) => console.log(`Error on fetching remote data: ${reason}`))
  );
}

export function fetchChangeDocumentPartStructure(
  structureDocumentPart: TgkChangeDocStructureElaborationRequest
): Promise<TgkElaborationResponse> {
  return axiosInstance
    .post(`structuredocumentpart/changedocumentstructure`, structureDocumentPart)
    .then((resp) => resp.data);
}
export function fetchExpandedDocumentPartOutline(id: string): Promise<DocumentPart> {
  return (
    axiosInstance
      .get(`${BASE_PATH}/expandedoutline/${id}`)
      .then((resp) => resp.data) // eslint-disable-next-line no-undef
      // eslint-disable-next-line no-undef
      .catch((reason) => console.log(`Error on fetching remote data: ${reason}`))
  );
}

export function fetchDriveItemName(driveItemId: string): Promise<string> {
  return (
    axiosInstance
      .get(`${BASE_PATH}/${driveItemId}/sharepointname`)
      .then((resp) => resp.data) // eslint-disable-next-line no-undef
      // eslint-disable-next-line no-undef
      .catch((reason) => console.log(`Error on fetching remote data: ${reason}`))
  );
}

export function editDocumentPart(request: DocumentPart): Promise<DocumentPart> {
  return axiosInstance.put(`${BASE_PATH}/edit`, request).then((resp) => resp.data);
}

export function fetchParentsDocumentPart(linkedPartId: string): Promise<DocumentPart[]> {
  return axiosInstance.get(`${BASE_PATH}/${linkedPartId}/fathers`).then((resp) => resp.data);
}

export function fetchAllDocumentPartStatus(documentPartId: string): Promise<boolean> {
  return axiosInstance.get(`${BASE_PATH}/${documentPartId}/alldocumentpartstatus`).then((resp) => resp.data);
}

export function fetchRecursiveParentsDocumentPart(linkedPartId: string): Promise<DocumentPosition[]> {
  return axiosInstance.get(`${BASE_PATH}/${linkedPartId}/recursivefathers`).then((resp) => resp.data);
}

export function fetchRootsDocumentPart(documentId: string): Promise<DocumentPart[]> {
  return axiosInstance.get(`${BASE_PATH}/${documentId}/roots`).then((resp) => resp.data);
}

export function fetchDocumentMetadata(linkedPartId: string): Promise<DocumentPartMetadata> {
  return axiosInstance.get(`${BASE_PATH}metadata/${linkedPartId}/activedocumentpartmetadata`).then((resp) => resp.data);
}

export function unlinkDocumentPart(docPartId: string, linkedDocPartId: string, order: number): Promise<string> {
  return axiosInstance.post(`${BASE_PATH}/${docPartId}/${linkedDocPartId}/${order}/unlink`).then((resp) => resp.data);
}

export function fetchCheckUnlinkPart(linkedPartId: string): Promise<boolean> {
  return axiosInstance.get(`${BASE_PATH}/${linkedPartId}/checkunlink`).then((resp) => resp.data);
}

export function fetchCheckDeletePart(documentPartId: string): Promise<string[]> {
  return axiosInstance.get(`${BASE_PATH}/${documentPartId}/checkdelete`).then((resp) => resp.data);
}
export function deleteDocumentPart(
  docPartId: string,
  linkedDocPartId: string,
  deleteFromClient?: boolean
): Promise<string[]> {
  return axiosInstance
    .delete(
      `${BASE_PATH}/${docPartId}/${linkedDocPartId}/delete${
        deleteFromClient ? "?deleteContentControlFromClient=true" : ""
      }`
    )
    .then((resp) => resp.data);
}

export function fetchDocumentsDbList(documentDbList: Object): Promise<Map<string, DocumentPartIdNamePair[]>> {
  return axiosInstance.post(`${BASE_PATH}/documentsdblist`, documentDbList).then((resp) => resp.data);
}

export function fetchDbMapping(documentDbList: Object): Promise<CallbackMappingMessage> {
  return axiosInstance.post(`${BASE_PATH}/dbmapping`, documentDbList).then((resp) => resp.data);
}
